/*
 * Lob Analysis — where the selected players' lobs land in the OPPONENTS' half.
 *
 * Court orientation is the whole point of the card: the net is at the bottom
 * and the back glass at the top, so depth (measured from the net, 0 m at the
 * net) increases upwards and a deeper lob visually travels further away from
 * the opponents. The service line is drawn at the backend's own
 * `service_line_depth_m` and splits the half into the two regions whose
 * percentages are printed inside them.
 *
 * Only aggregates are drawn — never invented landing points. Average height
 * and the success rate sit BELOW the court on purpose: placing them on the
 * diagram would imply a spatial meaning they don't have.
 */

import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { cn } from '@/utils/cn';
import { DivergingBars, type DivergingDatum } from './StyleOfPlay';
import { CompareGroup, type CmpDatum, type MetricSpec } from './CompareBars';
import type { LobAnalytics, LobSelection } from './shotAnalytics';

/** One comparison row: a player, or a team aggregated from its own members. */
export interface LobCompareEntity {
  id: string;
  name: string;
  color: string;
  crop?: string;
  /** Team rows: the members' crops, shown as a small avatar cluster. */
  crops?: string[];
  metrics: LobAnalytics | null;
}

/** Opponent half: net (0 m) → back glass (10 m), 10 m wide. */
const COURT_DEPTH_M = 10;
const VB = 200;

const SELECTIONS: LobSelection[] = ['both', 'forehand_lob', 'backhand_lob'];

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/** Depth in metres → share of the diagram's height from the top. */
const depthToPct = (depthM: number) =>
  clamp((1 - depthM / COURT_DEPTH_M) * 100, 0, 100);

const pctText = (rate: number | null) =>
  rate === null ? null : `${Math.round(rate * 100)}%`;

const LobSelector: React.FC<{
  selection: LobSelection;
  onSelect: (next: LobSelection) => void;
}> = ({ selection, onSelect }) => {
  const { t } = useTranslation('tournament');
  return (
    <div
      className="lob-filter"
      role="group"
      aria-label={t('lobs.filter.label') as string}
    >
      {SELECTIONS.map((key) => {
        const active = key === selection;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={active}
            className={cn('lob-filter-btn', active && 'active')}
            onClick={() => onSelect(key)}
          >
            {t(`lobs.filter.${key}` as never) as string}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Comparison mode — two or more entities.
 *
 * A shared court would be a lie here: it would imply that every selected
 * player's lobs landed in one pooled distribution. Instead each entity keeps
 * its own row, reusing the report's existing split-pill language (behind the
 * service line on the left, in front on the right) and the standard comparison
 * bars for the metrics that carry no spatial meaning.
 *
 * Rows are aggregated strictly inside their own entity — a team row weights
 * only that team's two members — and an entity without comparable landing
 * positions is simply absent rather than drawn as 0%.
 */
const LobComparison: React.FC<{
  entities: LobCompareEntity[];
  selection: LobSelection;
  onSelect: (next: LobSelection) => void;
}> = ({ entities, selection, onSelect }) => {
  const { t } = useTranslation('tournament');

  const populated = entities.filter((entity) => entity.metrics?.hasData);

  const splitRows: DivergingDatum[] = populated
    .filter(
      (entity) =>
        entity.metrics!.serviceLine.state === 'compatible' &&
        entity.metrics!.behindServiceLinePct !== null &&
        entity.metrics!.inFrontServiceLinePct !== null &&
        entity.metrics!.behindServiceLinePct! +
          entity.metrics!.inFrontServiceLinePct! >
          0,
    )
    .map((entity) => ({
      id: entity.id,
      name: entity.name,
      color: entity.color,
      crop: entity.crop,
      crops: entity.crops,
      left: entity.metrics!.behindServiceLinePct!,
      right: entity.metrics!.inFrontServiceLinePct!,
    }));

  // Absent metrics stay absent: an entity missing a value drops out of that
  // single row, and a metric nobody measured drops out entirely.
  const metricRow = (
    label: string,
    pick: (m: LobAnalytics) => number | null,
    format: (value: number) => { display: string; unit: string },
  ): MetricSpec | null => {
    const data: CmpDatum[] = populated
      .map((entity) => ({ entity, value: pick(entity.metrics!) }))
      .filter((row): row is { entity: LobCompareEntity; value: number } =>
        Number.isFinite(row.value as number),
      )
      .map(({ entity, value }) => ({
        id: entity.id,
        name: entity.name,
        color: entity.color,
        crop: entity.crop,
        crops: entity.crops,
        value,
        ...format(value),
      }));
    return data.length ? { label, data } : null;
  };

  const metres = (value: number) => ({
    display: value.toFixed(1),
    unit: t('lobs.metersUnit') as string,
  });

  const metrics: MetricSpec[] = [
    metricRow(t('lobs.avgDepth') as string, (m) => m.averageDepthM, metres),
    metricRow(t('lobs.avgHeight') as string, (m) => m.averageHeightM, metres),
    metricRow(
      t('lobs.success') as string,
      (m) => m.successPct,
      (value) => ({
        display: `${Math.round(value * 100)}`,
        unit: '%',
      }),
    ),
  ].filter((row): row is MetricSpec => !!row);

  return (
    <div className="card lob-card">
      <div className="eyebrow">
        <span className="eyebrow-pt">{t('lobs.title')}</span>
      </div>
      <p className="lob-sub">{t('lobs.compareSubtitle')}</p>

      <LobSelector selection={selection} onSelect={onSelect} />

      {!populated.length ? (
        <p className="lob-empty">{t('lobs.noneForType')}</p>
      ) : (
        <>
          {splitRows.length ? (
            <DivergingBars
              forceIdentity
              hideFoot
              data={splitRows}
              left={{
                label: t('lobs.behindShort') as string,
                foot: '',
                fill: 'var(--accent)',
                ink: 'var(--accent-ink)',
              }}
              right={{
                label: t('lobs.inFrontShort') as string,
                foot: '',
                fill: 'var(--accent-deep)',
                ink: 'var(--accent-deep)',
              }}
            />
          ) : (
            <p className="lob-empty">{t('lobs.noSplitRows')}</p>
          )}

          {metrics.length ? (
            <div className="lob-compare-metrics">
              <CompareGroup metrics={metrics} />
            </div>
          ) : null}

          <p className="lob-hint">
            <Info size={12} strokeWidth={2.2} aria-hidden />
            <span>{t('lobs.successHint')}</span>
          </p>
        </>
      )}
    </div>
  );
};

const LobAnalysis: React.FC<{
  metrics: LobAnalytics | null;
  selection: LobSelection;
  onSelect: (next: LobSelection) => void;
  /** Two or more entities → per-row comparison instead of a shared court. */
  comparison?: LobCompareEntity[];
}> = ({ metrics, selection, onSelect, comparison }) => {
  const { t } = useTranslation('tournament');

  if (comparison && comparison.length > 1) {
    return (
      <LobComparison
        entities={comparison}
        selection={selection}
        onSelect={onSelect}
      />
    );
  }

  const populated = !!metrics?.hasData;
  const serviceDepth =
    metrics?.serviceLine.state === 'compatible'
      ? metrics.serviceLine.depthM
      : null;
  const serviceLineIncompatible = metrics?.serviceLine.state === 'incompatible';
  const successIncompatible =
    metrics?.successThreshold.state === 'incompatible';

  const behind = pctText(metrics?.behindServiceLinePct ?? null);
  const inFront = pctText(metrics?.inFrontServiceLinePct ?? null);
  const success = pctText(metrics?.successPct ?? null);
  const height = metrics?.averageHeightM ?? null;
  const depth = metrics?.averageDepthM ?? null;

  const serviceTop = serviceDepth !== null ? depthToPct(serviceDepth) : null;
  const depthTop = depth !== null ? depthToPct(depth) : null;

  // A compatible service-line threshold says nothing about whether any lob was
  // actually located: with no position measurements there is nothing to place
  // on the court, so drawing it would only produce empty regions. Decide on
  // the position-derived metrics themselves.
  const hasPositions = depth !== null || behind !== null || inFront !== null;
  const courtDrawn =
    populated &&
    !serviceLineIncompatible &&
    serviceTop !== null &&
    hasPositions;
  // Average depth is independent of the service-line threshold, so it survives
  // an incompatible court. In the normal state the on-court marker already
  // carries it and it is not repeated below.
  const showDepthStat = !courtDrawn && depth !== null;
  /** Non-null exactly when the court is drawable (narrows for the SVG below). */
  const courtServiceTop = courtDrawn ? serviceTop : null;

  // Region captions sit in the middle of their own band, clamped away from the
  // court edges so a service line close to the net or the glass still reads.
  const behindTop = serviceTop !== null ? clamp(serviceTop / 2, 10, 90) : null;
  const frontTop =
    serviceTop !== null ? clamp((serviceTop + 100) / 2, 10, 90) : null;

  const courtSummary = [
    t('lobs.title') as string,
    behind ? `${t('lobs.behind')}: ${behind}` : null,
    inFront ? `${t('lobs.inFront')}: ${inFront}` : null,
    depth !== null
      ? `${t('lobs.avgDepth')}: ${depth.toFixed(1)} ${t('lobs.metersUnit')}`
      : null,
  ]
    .filter(Boolean)
    .join('. ');

  return (
    <div className="card lob-card">
      <div className="eyebrow">
        <span className="eyebrow-pt">{t('lobs.title')}</span>
      </div>
      <p className="lob-sub">{t('lobs.subtitle')}</p>

      <LobSelector selection={selection} onSelect={onSelect} />

      {!populated ? (
        <p className="lob-empty">{t('lobs.noneForType')}</p>
      ) : (
        <>
          {courtServiceTop === null ? (
            <p className="lob-empty">
              {serviceLineIncompatible
                ? t('lobs.incompatibleCourt')
                : t('lobs.noPositions')}
            </p>
          ) : (
            <div className="lob-court-wrap">
              <svg
                className="lob-court"
                viewBox={`0 0 ${VB} ${VB}`}
                role="img"
                aria-label={courtSummary}
              >
                {/* opponent half */}
                <rect
                  x={0}
                  y={0}
                  width={VB}
                  height={VB}
                  fill="var(--court-1)"
                />
                {/* behind-the-service-line band — the deep target zone */}
                <rect
                  x={0}
                  y={0}
                  width={VB}
                  height={(courtServiceTop / 100) * VB}
                  fill="var(--accent-soft)"
                />
                {/* centre service line, net → service line (padel markings) */}
                <line
                  x1={VB / 2}
                  y1={(courtServiceTop / 100) * VB}
                  x2={VB / 2}
                  y2={VB}
                  stroke="var(--court-line)"
                  strokeWidth={1}
                  opacity={0.3}
                />
                {/* the service line itself — the card's spatial anchor */}
                <line
                  x1={0}
                  y1={(courtServiceTop / 100) * VB}
                  x2={VB}
                  y2={(courtServiceTop / 100) * VB}
                  stroke="var(--court-line)"
                  strokeWidth={2.5}
                />
                {/* average depth of the selected lobs */}
                {depthTop !== null ? (
                  <line
                    x1={0}
                    y1={(depthTop / 100) * VB}
                    x2={VB}
                    y2={(depthTop / 100) * VB}
                    stroke="var(--accent)"
                    strokeWidth={2}
                    strokeDasharray="7 5"
                  />
                ) : null}
                <rect
                  x={0.75}
                  y={0.75}
                  width={VB - 1.5}
                  height={VB - 1.5}
                  fill="none"
                  stroke="var(--court-line)"
                  strokeWidth={1.5}
                />
                {/* net band along the bottom edge */}
                <rect
                  x={0}
                  y={VB - 5}
                  width={VB}
                  height={5}
                  fill="var(--court-line)"
                  opacity={0.85}
                />
              </svg>

              <span className="lob-edge is-top">{t('lobs.baseline')}</span>
              <span className="lob-edge is-bottom">{t('lobs.net')}</span>

              <span
                className="lob-service-tag"
                style={{ top: `${courtServiceTop}%` }}
              >
                {t('lobs.serviceLine')}
              </span>

              {behindTop !== null ? (
                <div className="lob-region" style={{ top: `${behindTop}%` }}>
                  <span className="lob-region-pct">{behind ?? '—'}</span>
                  <span className="lob-region-label">{t('lobs.behind')}</span>
                </div>
              ) : null}
              {frontTop !== null ? (
                <div className="lob-region" style={{ top: `${frontTop}%` }}>
                  <span className="lob-region-pct">{inFront ?? '—'}</span>
                  <span className="lob-region-label">{t('lobs.inFront')}</span>
                </div>
              ) : null}

              {depthTop !== null && depth !== null ? (
                <span className="lob-depth-tag" style={{ top: `${depthTop}%` }}>
                  {t('lobs.avgDepth')} {depth.toFixed(1)}
                  {t('lobs.metersUnit')}
                </span>
              ) : null}
            </div>
          )}

          {/* Height and success live below the court — they carry no position.
              Average depth joins them only when the court could not be drawn,
              so a threshold disagreement never swallows a metric that doesn't
              depend on that threshold. */}
          <div className="lob-stats">
            {showDepthStat ? (
              <>
                <div className="lob-stat">
                  <span className="lob-stat-value">
                    {depth!.toFixed(1)}
                    <span className="lob-stat-unit">
                      {t('lobs.metersUnit')}
                    </span>
                  </span>
                  <span className="lob-stat-label">{t('lobs.avgDepth')}</span>
                </div>
                <span className="lob-stat-divider" aria-hidden />
              </>
            ) : null}
            <div className="lob-stat">
              <span className="lob-stat-value">
                {height !== null ? (
                  <>
                    {height.toFixed(1)}
                    <span className="lob-stat-unit">
                      {t('lobs.metersUnit')}
                    </span>
                  </>
                ) : (
                  <span className="lob-stat-na">{t('lobs.unavailable')}</span>
                )}
              </span>
              <span className="lob-stat-label">{t('lobs.avgHeight')}</span>
            </div>
            <span className="lob-stat-divider" aria-hidden />
            <div className="lob-stat">
              <span className="lob-stat-value">
                {success !== null ? (
                  success
                ) : (
                  <span className="lob-stat-na">{t('lobs.unavailable')}</span>
                )}
              </span>
              <span className="lob-stat-label">{t('lobs.success')}</span>
            </div>
          </div>

          <p className="lob-hint">
            <Info size={12} strokeWidth={2.2} aria-hidden />
            <span>
              {successIncompatible
                ? t('lobs.incompatibleSuccess')
                : t('lobs.successHint')}
            </span>
          </p>

          {metrics?.availability === 'partial' ? (
            <p className="lob-partial">{t('lobs.partial')}</p>
          ) : null}
        </>
      )}
    </div>
  );
};

export default LobAnalysis;
